import { useMemo } from 'react'
import styled from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { EditIcon, Skeleton, useHapticFeedback } from '@blade/ui-kit'
import { Numeric } from '@blade/ui-kit'
import { SuggestedGasSettings } from '@root/entities/gas-information'
import { GasSettingVariantIcon } from '@root/entities/gas-information/ui'
import { useDateFnsLocale, useTranslation } from '@root/shared/localization'

import { GAS_VARIANT_LABEL_MAP } from '../../common/constants'

import type { GasSettingsSelection } from '../../../atoms/transfer-fee-reatom'
import type BigNumber from 'bignumber.js'

type TransferFeeEditButtonProps = {
  settingsSelection: GasSettingsSelection
  estimatedGasFeeUsd: BigNumber | undefined
  onEdit?: () => void
}

export const TransferFeeEditButton: React.FC<TransferFeeEditButtonProps> = ({
  settingsSelection,
  estimatedGasFeeUsd,
  onEdit,
}) => {
  const { t } = useTranslation()
  const haptics = useHapticFeedback()
  const locale = useDateFnsLocale()

  const settingsState = useMemo(() => {
    return settingsSelection.type === 'Recommended'
      ? settingsSelection.settings
      : AsyncStateN.success(settingsSelection.settings)
  }, [settingsSelection.settings, settingsSelection.type])

  const renderAverageWaitTime = () => {
    if (!AsyncStateN.isSuccess(settingsState)) {
      return <Skeleton height={16} paddingBlock={2} width={40} />
    }

    const settings = settingsState.value
    const waitTime = SuggestedGasSettings.formatWaitTime(
      settings.averageWaitTimeEstimate,
      locale,
    )
    const formatted = `~ ${waitTime}`
    return <WaitTimeValue>{formatted}</WaitTimeValue>
  }

  const variant =
    settingsSelection.type === 'Recommended'
      ? settingsSelection.variant
      : 'advanced'

  const labelKey = GAS_VARIANT_LABEL_MAP[variant]

  return (
    <Root
      type="button"
      onClick={() => {
        if (onEdit) {
          haptics.impactOccurred('light')
          onEdit()
        }
      }}
    >
      <StartContent>
        <VariantContainer>
          <GasSettingVariantIcon size={16} variant={variant} />
          <VariantText>{t(labelKey)}</VariantText>
        </VariantContainer>
        <WaitTime>{renderAverageWaitTime()}</WaitTime>
      </StartContent>
      <EndContent>
        <PriceContainer>
          <EditIcon size={12} />
          <GasPrice>
            {estimatedGasFeeUsd ? (
              <Numeric
                value={estimatedGasFeeUsd}
                currency="$"
                preset="fee-usd"
              />
            ) : undefined}
          </GasPrice>
        </PriceContainer>
        <GasConst>{t('gas-settings.gas-cost')}</GasConst>
      </EndContent>
    </Root>
  )
}

const Root = styled.button`
  all: unset;
  cursor: pointer;
  border-radius: 12px;
  height: 52px;
  padding-inline: 11px;
  background: var(--appearance-1);

  display: grid;
  align-content: center;
  align-items: center;
  grid-template-columns: auto auto;
`

const StartContent = styled.div`
  border-inline-end: 1px solid #171717;
  padding-inline: 0 10px;
  display: grid;
  justify-items: center;
`

const VariantContainer = styled.div`
  display: grid;
  align-items: center;
  grid-template-columns: auto auto;
  grid-gap: 6px;
`

const VariantText = styled.div`
  color: var(--text-12);
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
`

const GasConst = styled.div`
  color: var(--text-9);
  text-align: center;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;

  white-space: nowrap;
`

const WaitTime = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  align-items: center;
  gap: 2px;
`

const WaitTimeValue = styled.div`
  color: var(--text-12);
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  white-space: nowrap;
`

const EndContent = styled.div`
  padding-inline: 10px 0;
  display: grid;
  justify-items: center;
`

const PriceContainer = styled.div`
  display: grid;
  grid-gap: 3px;
  grid-template-columns: auto 1fr;
  align-items: center;
  color: #ffbf00;
`
const GasPrice = styled.div`
  font-size: 10px;
  font-weight: 600;
  line-height: 12px;
`
